import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Link2, Plus, Eye, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { newDraftId, type SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import type { ScenarioDisplayLayout } from "@/lib/powtoon-be/types";
import { LearnerDisplayPreviewModal } from "./LearnerDisplayPreviewModal";
import { BUBBLE_ASPECT_CLASS } from "@/components/session/bubble";
import { UrlEmbedStatus, useUrlEmbedCheck } from "./UrlEmbedStatus";
import {
  checkUrlEmbeddable,
  isDisplayableVerdict,
  normalizeDisplayUrl,
} from "@/lib/scenarios/urlEmbedCheck";

const BLOCKED_MESSAGE =
  "This page refuses to be embedded, so learners would see nothing. Use a PDF, an image, or a different URL.";
const UNREACHABLE_MESSAGE =
  "We couldn't reach this URL, so learners would see nothing. Check the address or use a file instead.";

const DISPLAY_FILE_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.gif,.webp,.mp4,.webm,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation";

export type DisplaySelection =
  | { kind: "upload"; draftId: string }
  | { kind: "url"; url: string };

export type DisplayFormValue = {
  layout: ScenarioDisplayLayout;
  avatarPosition: "left" | "right";
  selection?: DisplaySelection;
};

function LayoutThumb({
  layout,
  active,
  avatarPosition,
}: {
  layout: ScenarioDisplayLayout;
  active: boolean;
  avatarPosition: "left" | "right";
}) {
  const avatarOnLeft = avatarPosition === "left";
  return (
    <div
      className={cn(
        "relative w-full aspect-[16/10] rounded-md border-2 bg-muted/30 overflow-hidden",
        active ? "border-primary" : "border-border",
      )}
    >
      {layout === "side-by-side" ? (
        <div className={cn("flex h-full w-full", avatarOnLeft && "flex-row-reverse")}>
          <div
            className={cn(
              "flex-[2] bg-primary/15",
              avatarOnLeft ? "border-l border-border" : "border-r border-border",
            )}
          />
          <div className="flex-1 bg-primary/40" />
        </div>
      ) : (
        <>
          <div className="absolute inset-0 bg-primary/15" />
          <div
            className={cn(
              "absolute top-1.5 w-[21%] rounded-md bg-primary/70 border-2 border-background shadow",
              BUBBLE_ASPECT_CLASS,
              avatarOnLeft ? "left-1.5" : "right-1.5",
            )}
          />
        </>
      )}
    </div>
  );
}

function describeDraft(d: SourceInputDraft): string | null {
  if (d.kind === "upload") return d.file_name;
  if (d.kind === "url") return d.url.trim() || null;
  return null;
}

export function LearnerDisplayPicker({
  value,
  onChange,
  sources,
  onSourcesChange,
  scenarioId,
  avatarThumbUrl,
}: {
  value: DisplayFormValue | null;
  onChange: (next: DisplayFormValue | null) => void;
  sources: SourceInputDraft[];
  onSourcesChange: (next: SourceInputDraft[]) => void;
  scenarioId?: string;
  avatarThumbUrl?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  // Only sources the trainer added from inside this picker are removable here;
  // the scenario's own source inputs are managed in the section above.
  const [ownDraftIds, setOwnDraftIds] = useState<Set<string>>(new Set());

  const enabled = value !== null;
  const layout = value?.layout ?? "side-by-side";
  const avatarPosition: "left" | "right" = value?.avatarPosition ?? "right";
  const selection = value?.selection;

  // Build options from current sources (uploads + urls only).
  const options = sources
    .filter((d) => d.kind === "upload" || (d.kind === "url" && d.url.trim().length > 0))
    .map((d) => ({
      draft: d,
      value:
        d.kind === "upload"
          ? `u:${d._draftId}`
          : `r:${(d as Extract<SourceInputDraft, { kind: "url" }>).url.trim()}`,
      label: describeDraft(d) ?? "(unnamed)",
      icon: d.kind,
    }));

  const selectedValue: string =
    selection?.kind === "upload"
      ? `u:${selection.draftId}`
      : selection?.kind === "url"
        ? `r:${selection.url}`
        : "";

  // Warn if selection no longer exists in sources.
  const selectionMissing =
    !!selection && !options.some((o) => o.value === selectedValue);

  // Probe framing headers whenever a URL source is selected, so the trainer
  // learns up-front that a site refuses to be embedded.
  const embedTarget =
    selection?.kind === "url" && !selectionMissing ? selection.url : undefined;
  const embed = useUrlEmbedCheck(embedTarget);

  function setEnabled(b: boolean) {
    if (!b) onChange(null);
    else onChange({ layout, avatarPosition, selection });
  }

  function setLayout(next: ScenarioDisplayLayout) {
    onChange({ layout: next, avatarPosition, selection });
  }

  function setSelection(next: DisplaySelection | undefined) {
    onChange({ layout, avatarPosition, selection: next });
  }

  function setAvatarPosition(next: "left" | "right") {
    onChange({ layout, avatarPosition: next, selection });
  }

  async function handleSelectChange(val: string) {
    if (val === "__upload__") {
      fileRef.current?.click();
      return;
    }
    if (val === "__url__") {
      setUrlError(null);
      setShowUrlInput(true);
      return;
    }
    const opt = options.find((o) => o.value === val);
    if (!opt) return;
    if (opt.draft.kind === "upload") {
      setUrlError(null);
      setSelection({ kind: "upload", draftId: opt.draft._draftId });
    } else if (opt.draft.kind === "url") {
      const url = opt.draft.url.trim();
      setCheckingUrl(true);
      try {
        const res = await checkUrlEmbeddable(url);
        if (!isDisplayableVerdict(res.verdict)) {
          const msg = res.verdict === "blocked" ? BLOCKED_MESSAGE : UNREACHABLE_MESSAGE;
          setUrlError(msg);
          toast.error(msg);
          return; // keep the previous selection
        }
        setUrlError(null);
        setSelection({ kind: "url", url });
      } finally {
        setCheckingUrl(false);
      }
    }
  }

  function handleNewFile(file: File) {
    const draftId = newDraftId();
    const next: SourceInputDraft = {
      kind: "upload",
      _draftId: draftId,
      file_name: file.name,
      size: file.size,
      mime: file.type || undefined,
      _file: file,
    };
    onSourcesChange([...sources, next]);
    setOwnDraftIds((prev) => new Set(prev).add(draftId));
    setUrlError(null);
    setSelection({ kind: "upload", draftId });
  }

  function removeDraft(draft: SourceInputDraft) {
    onSourcesChange(sources.filter((d) => d._draftId !== draft._draftId));
    setOwnDraftIds((prev) => {
      const next = new Set(prev);
      next.delete(draft._draftId);
      return next;
    });
    const wasSelected =
      (selection?.kind === "upload" && selection.draftId === draft._draftId) ||
      (selection?.kind === "url" &&
        draft.kind === "url" &&
        draft.url.trim() === selection.url);
    if (wasSelected) {
      setUrlError(null);
      setSelection(undefined);
    }
  }

  async function commitNewUrl() {
    const trimmed = normalizeDisplayUrl(urlDraft);
    if (!trimmed) return;
    setCheckingUrl(true);
    setUrlError(null);
    let res;
    try {
      res = await checkUrlEmbeddable(trimmed);
    } finally {
      setCheckingUrl(false);
    }
    if (!isDisplayableVerdict(res.verdict)) {
      setUrlError(res.verdict === "blocked" ? BLOCKED_MESSAGE : UNREACHABLE_MESSAGE);
      return;
    }
    const existing = sources.find(
      (d) => d.kind === "url" && d.url.trim() === trimmed,
    );
    if (!existing) {
      const draftId = newDraftId();
      onSourcesChange([
        ...sources,
        { kind: "url", _draftId: draftId, url: trimmed },
      ]);
      setOwnDraftIds((prev) => new Set(prev).add(draftId));
    }
    setSelection({ kind: "url", url: trimmed });
    setUrlDraft("");
    setShowUrlInput(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <Label htmlFor="display-toggle">Show a document to the learner</Label>
          <p className="text-xs text-muted-foreground">
            Pick a source the learner sees alongside the avatar. Choose an existing
            source input or add a new one.
          </p>
        </div>
        <Switch id="display-toggle" checked={enabled} onCheckedChange={setEnabled} />
      </div>

      {enabled && (
        <div className="space-y-4 rounded-md border bg-background p-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Source</Label>
            <Select
              value={selectionMissing ? "" : selectedValue}
              onValueChange={(v) => void handleSelectChange(v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a source…" />
              </SelectTrigger>
              <SelectContent>
                {options.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    No reusable sources yet.
                  </div>
                )}
                {options.map((o) => (
                  <div key={o.value} className="flex items-center gap-1">
                    <SelectItem value={o.value} className="flex-1">
                      <span className="inline-flex items-center gap-2">
                        {o.icon === "upload" ? (
                          <Upload className="h-3.5 w-3.5" />
                        ) : (
                          <Link2 className="h-3.5 w-3.5" />
                        )}
                        <span className="truncate max-w-[230px]">{o.label}</span>
                      </span>
                    </SelectItem>
                    {ownDraftIds.has(o.draft._draftId) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${o.label}`}
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeDraft(o.draft);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <SelectItem value="__upload__">
                  <span className="inline-flex items-center gap-2">
                    <Plus className="h-3.5 w-3.5" />
                    <Upload className="h-3.5 w-3.5" />
                    Upload new file…
                  </span>
                </SelectItem>
                <SelectItem value="__url__">
                  <span className="inline-flex items-center gap-2">
                    <Plus className="h-3.5 w-3.5" />
                    <Link2 className="h-3.5 w-3.5" />
                    Add new URL…
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <input
              ref={fileRef}
              type="file"
              accept={DISPLAY_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleNewFile(f);
                e.target.value = "";
              }}
            />
            {showUrlInput && (
              <div className="flex items-center gap-2 pt-1">
                <Input
                  type="url"
                  placeholder="https://example.com/document.pdf"
                  value={urlDraft}
                  onChange={(e) => {
                    setUrlDraft(e.target.value);
                    if (urlError) setUrlError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void commitNewUrl();
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void commitNewUrl()}
                  disabled={checkingUrl || !urlDraft.trim()}
                >
                  {checkingUrl && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  {checkingUrl ? "Checking…" : "Add"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={checkingUrl}
                  onClick={() => {
                    setShowUrlInput(false);
                    setUrlDraft("");
                    setUrlError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {urlError && <p className="text-xs text-destructive">{urlError}</p>}
            {selectionMissing && (
              <p className="text-xs text-destructive">
                The previously selected source is no longer available. Pick another one.
              </p>
            )}
            {embedTarget && (
              <UrlEmbedStatus
                loading={embed.loading}
                result={embed.result}
                onRecheck={embed.recheck}
                className="pt-0.5"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Layout</Label>
            <div className="grid grid-cols-2 gap-3">
              {(["side-by-side", "bubble"] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLayout(l)}
                  className={cn(
                    "text-left space-y-1 rounded-md p-2 transition-colors",
                    layout === l ? "bg-primary/5" : "hover:bg-muted/40",
                  )}
                >
                  <LayoutThumb layout={l} active={layout === l} avatarPosition={avatarPosition} />
                  <div className="text-xs font-medium">
                    {l === "side-by-side" ? "Side by side" : "Document with avatar bubble"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Avatar position</Label>
            <div className="inline-flex rounded-md border bg-background p-0.5">
              {(["left", "right"] as const).map((pos) => (
                <button
                  key={pos}
                  type="button"
                  onClick={() => setAvatarPosition(pos)}
                  className={cn(
                    "px-3 py-1 text-xs rounded-sm transition-colors capitalize",
                    avatarPosition === pos
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted/40",
                  )}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPreviewOpen(true)}
              disabled={!selection}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Preview
            </Button>
          </div>

          <LearnerDisplayPreviewModal
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            layout={layout}
            avatarPosition={avatarPosition}
            selection={selection}
            sources={sources}
            scenarioId={scenarioId}
            avatarThumbUrl={avatarThumbUrl}
          />
        </div>
      )}
    </div>
  );
}